import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { Shield, CheckCircle2, XCircle, Loader2 } from "lucide-react";

const STATUS_COLOR = {
  active: { label: "ACTIVO", color: "#34C759" },
  injured: { label: "LESIONADO", color: "#FF9F0A" },
  suspended: { label: "SUSPENDIDO", color: "#FF3B30" },
};

const CATEGORY_LABEL = {
  tackle: "Tackle",
  flag_masculino: "Flag Masculino",
  flag_femenino: "Flag Femenino",
};

const ROLE_LABEL = {
  super_admin: "Super Admin",
  admin: "Administrador",
  admin_directive: "Admin Directivo",
  admin_treasurer: "Admin Tesorero",
  coach: "Coach",
  player: "Jugador",
};

export default function CredentialValidation() {
  const { userId } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    const url = `${process.env.REACT_APP_BACKEND_URL}/api/credential/${userId}`;
    axios.get(url)
      .then(r => setData(r.data))
      .catch(() => setError("Credencial no encontrada o inválida"));
  }, [userId]);

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center p-6" style={{ background: "linear-gradient(135deg, #0a1628 0%, #050b16 100%)" }}>
        <div className="max-w-md w-full text-center text-white p-8 border-2 border-red-500/40" data-testid="credential-invalid">
          <XCircle size={48} className="text-red-500 mx-auto mb-4"/>
          <div className="font-display text-2xl mb-2">CREDENCIAL INVÁLIDA</div>
          <div className="text-sm opacity-70">{error}</div>
        </div>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: "linear-gradient(135deg, #0a1628 0%, #050b16 100%)" }}>
        <Loader2 size={32} className="text-cyan-400 animate-spin"/>
      </div>
    );
  }

  const status = STATUS_COLOR[data.status] || STATUS_COLOR.active;

  return (
    <div className="min-h-screen flex items-center justify-center p-4" style={{ background: "linear-gradient(135deg, #0a1628 0%, #050b16 100%)" }} data-testid="credential-validation">
      <div className="max-w-md w-full text-white p-8 border-2 border-cyan-400 relative overflow-hidden" style={{ background: "rgba(10,22,40,0.9)" }}>
        {/* Verified ribbon */}
        <div className="flex items-center gap-2 mb-6 pb-4 border-b border-cyan-400/30">
          <CheckCircle2 size={20} className="text-cyan-400"/>
          <div className="text-xs uppercase tracking-widest font-bold text-cyan-400">Credencial Verificada</div>
        </div>

        {/* Status chip */}
        <div className="inline-block px-3 py-1 text-[10px] font-bold uppercase tracking-widest mb-4" style={{ background: status.color, color: "#0a0a0a" }} data-testid="cred-status">
          {status.label}
        </div>

        {/* Name */}
        <div className="font-display text-3xl mb-1 leading-tight" data-testid="cred-name">{data.name?.toUpperCase()}</div>
        <div className="text-sm text-cyan-400 mb-6" data-testid="cred-role">
          {ROLE_LABEL[data.role] || data.role}
          {data.categories?.length > 0 && (
            <span> · {data.categories.map(c => CATEGORY_LABEL[c] || c).join(" · ")}</span>
          )}
        </div>

        {/* Jersey + Position */}
        <div className="grid grid-cols-2 gap-4 mb-6">
          <DataField label="Camiseta" value={data.jersey_number ?? "—"} big/>
          <DataField label="Posición" value={data.position || "—"} big/>
        </div>

        {/* Email masked */}
        <div className="text-xs text-white/50 font-mono mb-4" data-testid="cred-email">{data.email_masked}</div>

        {/* Footer */}
        <div className="pt-4 border-t border-cyan-400/20 flex items-center gap-2 text-[10px] uppercase tracking-widest text-cyan-400/60">
          <Shield size={12}/>
          Verificado · {new Date(data.verified_at).toLocaleString("es-CL")}
        </div>
        <div className="text-[10px] text-white/40 mt-2">
          Ferroviarios Puente Alto — Club de Fútbol Americano
        </div>
      </div>
    </div>
  );
}

const DataField = ({ label, value, big }) => (
  <div>
    <div className="text-[10px] uppercase tracking-widest text-cyan-400/70 mb-1">{label}</div>
    <div className={`font-display ${big ? "text-2xl" : "text-base"} text-white`}>{value}</div>
  </div>
);
